// src/api/mockPatientApi.js
import {
  dashboardStatsMockData,
  appointmentHistoryMockData,
  healthSummaryMockData,
  recentAppointmentsMockData,
  upcomingAppointmentsMockData,
  prescriptionsMockData,
  healthMetricsMockData,
} from '../data/patientDashboardMockData';

// Simulate API delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Mock API for patient dashboard
export const mockPatientApi = {
  // Dashboard endpoints
  getDashboardStats: async () => {
    await delay(800); // Simulate network delay
    return { data: dashboardStatsMockData };
  },

  getAppointmentHistory: async (months = 6) => {
    await delay(600);
    return { data: appointmentHistoryMockData };
  },

  getHealthSummary: async () => {
    await delay(700);
    return { data: healthSummaryMockData };
  },

  // Additional endpoints that might be useful
  getRecentAppointments: async () => {
    await delay(500);
    return { data: recentAppointmentsMockData };
  },

  getUpcomingAppointments: async () => {
    await delay(500);
    return { data: upcomingAppointmentsMockData };
  },

  getPrescriptions: async () => {
    await delay(600);
    return { data: prescriptionsMockData };
  },

  getHealthMetrics: async () => {
    await delay(700);
    return { data: healthMetricsMockData };
  },

  // New Patient Dashboard Endpoints
  
  // Messaging
  getMessageThreads: async () => {
    await delay(500);
    return {
      data: {
        results: [
          {
            id: 'thread_1',
            participants: [
              {
                id: '1',
                name: 'John Doe',
                role: 'patient',
                avatar: null
              },
              {
                id: '2',
                name: 'Dr. Anil Sharma',
                role: 'doctor',
                avatar: null
              }
            ],
            last_message: {
              id: 1,
              text: 'Please ensure you take the medication on time.',
              sender_id: '2',
              created_at: '2024-01-15T10:40:00Z',
              is_read: false
            },
            unread_count: 2,
            created_at: '2024-01-15T10:30:00Z',
            updated_at: '2024-01-15T10:40:00Z'
          },
          {
            id: 'thread_2',
            participants: [
              {
                id: '1',
                name: 'John Doe',
                role: 'patient',
                avatar: null
              },
              {
                id: '3',
                name: 'Dr. Priya Mehra',
                role: 'doctor',
                avatar: null
              }
            ],
            last_message: {
              id: 2,
              text: 'Please upload the report for further review.',
              sender_id: '3',
              created_at: '2024-01-14T09:20:00Z',
              is_read: true
            },
            unread_count: 0,
            created_at: '2024-01-14T09:00:00Z',
            updated_at: '2024-01-14T09:20:00Z'
          }
        ],
        pagination: {
          count: 2,
          page: 1,
          page_size: 10
        }
      }
    };
  },

  getMessages: async (threadId) => {
    await delay(400);
    return {
      data: {
        results: [
          {
            id: 1,
            thread_id: threadId,
            sender: {
              id: '2',
              name: 'Dr. Anil Sharma',
              role: 'doctor',
              avatar: null
            },
            recipient: {
              id: '1',
              name: 'John Doe',
              role: 'patient',
              avatar: null
            },
            text: 'Hello Mr. Doe, how are you feeling today?',
            attachments: [],
            is_read: true,
            read_at: '2024-01-15T10:31:00Z',
            created_at: '2024-01-15T10:30:00Z',
            updated_at: '2024-01-15T10:30:00Z'
          },
          {
            id: 2,
            thread_id: threadId,
            sender: {
              id: '1',
              name: 'John Doe',
              role: 'patient',
              avatar: null
            },
            recipient: {
              id: '2',
              name: 'Dr. Anil Sharma',
              role: 'doctor',
              avatar: null
            },
            text: 'Good morning, Doctor. I\'m feeling better, but still have some anxiety.',
            attachments: [],
            is_read: true,
            read_at: '2024-01-15T10:36:00Z',
            created_at: '2024-01-15T10:35:00Z',
            updated_at: '2024-01-15T10:35:00Z'
          },
          {
            id: 3,
            thread_id: threadId,
            sender: {
              id: '2',
              name: 'Dr. Anil Sharma',
              role: 'doctor',
              avatar: null
            },
            recipient: {
              id: '1',
              name: 'John Doe',
              role: 'patient',
              avatar: null
            },
            text: 'That\'s good to hear. Please ensure you take the medication on time. I\'ll see you in our next session.',
            attachments: [],
            is_read: false,
            read_at: null,
            created_at: '2024-01-15T10:40:00Z',
            updated_at: '2024-01-15T10:40:00Z'
          }
        ],
        pagination: {
          count: 3,
          page: 1,
          page_size: 50
        }
      }
    };
  },

  sendMessage: async (data) => {
    await delay(300);
    return {
      data: {
        message: 'Message sent successfully',
        message_id: Math.floor(Math.random() * 1000),
        thread_id: data.thread_id || `thread_${data.recipient_id}`
      }
    };
  },

  // Billing
  getInvoices: async (params = {}) => {
    await delay(600);
    return {
      data: {
        results: [
          {
            id: '1',
            doctor: {
              id: '2',
              name: 'Dr. Anil Sharma',
              specialization: 'Psychiatrist'
            },
            patient_name: 'John Doe',
            consultation_date: '2024-01-10T10:00:00Z',
            amount: 1500.00,
            status: 'paid',
            created_at: '2024-01-10T10:30:00Z'
          },
          {
            id: '2',
            doctor: {
              id: '3',
              name: 'Dr. Priya Mehra',
              specialization: 'Clinical Psychologist'
            },
            patient_name: 'John Doe',
            consultation_date: '2024-01-08T14:00:00Z',
            amount: 1200.00,
            status: 'pending',
            created_at: '2024-01-08T14:30:00Z'
          }
        ],
        pagination: {
          count: 2,
          page: parseInt(params.page) || 1,
          page_size: parseInt(params.page_size) || 10,
          next: null,
          previous: null
        }
      }
    };
  },

  getInvoiceDetail: async (invoiceId) => {
    await delay(400);
    return {
      data: {
        id: invoiceId,
        doctor: {
          id: '2',
          name: 'Dr. Anil Sharma',
          specialization: 'Psychiatrist',
          clinic: 'MindWell Clinic'
        },
        patient_name: 'John Doe',
        consultation_date: '2024-01-10T10:00:00Z',
        amount: 1500.00,
        status: 'paid',
        created_at: '2024-01-10T10:30:00Z',
        payment_method: 'Online Payment',
        transaction_id: 'TXN_12345678'
      }
    };
  },

  downloadInvoicePDF: async (invoiceId) => {
    await delay(800);
    // Mock PDF blob
    const pdfBlob = new Blob(['Mock PDF content'], { type: 'application/pdf' });
    return { data: pdfBlob };
  },

  // Feedback
  getFeedbackHistory: async () => {
    await delay(500);
    return {
      data: {
        results: [
          {
            id: 1,
            doctor: {
              id: '2',
              name: 'Dr. Anil Sharma',
              specialization: 'Psychiatrist'
            },
            rating: 5.0,
            comment: 'Great consultation experience, very professional and attentive.',
            created_at: '2024-01-10T11:00:00Z'
          },
          {
            id: 2,
            doctor: {
              id: '3',
              name: 'Dr. Priya Mehra',
              specialization: 'Clinical Psychologist'
            },
            rating: 4.0,
            comment: 'Good session, but felt a bit rushed.',
            created_at: '2024-01-08T15:00:00Z'
          }
        ],
        pagination: {
          count: 2,
          page: 1,
          page_size: 10
        }
      }
    };
  },

  submitFeedback: async (doctorId, data) => {
    await delay(400);
    return {
      data: {
        message: 'Feedback submitted successfully',
        feedback_id: Math.floor(Math.random() * 1000)
      }
    };
  }
};
